import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { Project } from '../types/project.js';
import * as projectApi from './project-api.js';
import { router } from './router.js';
import { wsClient } from './websocket-client.js';

export type ProjectLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedProjects: Project[] = [];
let sharedActiveProject: Project | null = null;
let sharedLoadingState: ProjectLoadingState = 'idle';
let sharedError: string | null = null;
let sharedInitialized = false;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadProjects(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    sharedProjects = await projectApi.getProjects('active');
    sharedLoadingState = 'loaded';

    const params = router.getParams();
    if (params.id) {
      const id = Number(params.id);
      const match = sharedProjects.find(p => p.id === id);
      if (match) {
        sharedActiveProject = match;
      } else {
        try {
          sharedActiveProject = await projectApi.getProject(id);
        } catch {
          sharedActiveProject = null;
          sharedError = 'Projekt nicht gefunden';
        }
      }
    }
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Projekte konnten nicht geladen werden';
  }

  notifyHosts();
}

function onRouteChanged(): void {
  const params = router.getParams();
  if (params.id) {
    const id = Number(params.id);
    const match = sharedProjects.find(p => p.id === id);
    if (match) {
      sharedActiveProject = match;
      notifyHosts();
    } else if (sharedLoadingState === 'loaded') {
      projectApi.getProject(id).then(project => {
        sharedActiveProject = project;
        notifyHosts();
      }).catch(() => {
        sharedActiveProject = null;
        sharedError = 'Projekt nicht gefunden';
        notifyHosts();
      });
    }
  } else if (!router.currentPath.startsWith('/projects/')) {
    sharedActiveProject = null;
    notifyHosts();
  }
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string; payload: Project };
  if (detail.type === 'project:updated' && detail.payload) {
    const idx = sharedProjects.findIndex(p => p.id === detail.payload.id);
    if (idx !== -1) {
      sharedProjects = [...sharedProjects];
      sharedProjects[idx] = detail.payload;
    }
    if (sharedActiveProject?.id === detail.payload.id) {
      sharedActiveProject = detail.payload;
    }
    notifyHosts();
  }
}

router.addEventListener('route-changed', onRouteChanged);
wsClient.addEventListener('ws-message', onWsMessage);

export class ProjectStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
    if (!sharedInitialized) {
      sharedInitialized = true;
      void loadProjects();
    }
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get projects(): Project[] {
    return sharedProjects;
  }

  get activeProject(): Project | null {
    return sharedActiveProject;
  }

  get loadingState(): ProjectLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  setActiveProject(project: Project): void {
    sharedActiveProject = project;
    void projectApi.markProjectOpened(project.id);
    notifyHosts();
  }

  async refresh(): Promise<void> {
    await loadProjects();
  }
}
